import { z } from "zod";
import { AccountId, SubWalletId, WalletId } from "./ids.ts";
import { MoneyAmount } from "./money.ts";

/** Wallet status — `frozen` halts all spend authorization across sub-wallets. */
export const WalletStatus = z.enum(["active", "frozen"]);
export type WalletStatus = z.output<typeof WalletStatus>;

/**
 * Who froze a wallet (#119): `user` = a self-service freeze the owner can
 * lift; `operator` = a staff/chargeback freeze the owner must NOT be able to
 * lift (only an operator can unfreeze it). Null/absent when active.
 */
export const WalletFreezeActor = z.enum(["user", "operator"]);
export type WalletFreezeActor = z.output<typeof WalletFreezeActor>;

/**
 * Master wallet — one per account in MVP. Balance is the materialized view
 * of the ledger sum; reconciled by the cron job (#19). Schema-wise modeled
 * as composite-PK tenant so future multi-wallet/account isn't a rewrite.
 */
export const Wallet = z.object({
  id: WalletId,
  accountId: AccountId,
  balance: MoneyAmount,
  status: WalletStatus,
  /**
   * Who imposed the current freeze (#119). `operator` freezes are sticky —
   * a user-initiated unfreeze is rejected. Optional/nullable so existing
   * construction sites need no change; absent ⇒ no operator lock.
   */
  frozenBy: WalletFreezeActor.nullish(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type Wallet = z.output<typeof Wallet>;

/** Who's spending — `agent` triggers stricter caps + MCP token scoping (#10). */
export const SubWalletKind = z.enum(["human", "agent"]);
export type SubWalletKind = z.output<typeof SubWalletKind>;

/**
 * Sub-wallet — scoped child of a master wallet. Limits/approvals are
 * enforced server-side in `@ggcoin/wallet`; this is the data shape only.
 * Period semantics for `spendCap` (daily / rolling-24h / …) live in the
 * service, not in the type.
 */
export const SubWallet = z.object({
  id: SubWalletId,
  walletId: WalletId,
  accountId: AccountId,
  kind: SubWalletKind,
  label: z.string().min(1).max(80),
  balance: MoneyAmount,
  spendCap: MoneyAmount,
  approvalThreshold: MoneyAmount,
  status: WalletStatus,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type SubWallet = z.output<typeof SubWallet>;
